import type {
  CardFieldLayout,
  PaymentElementOptions,
  PaymentMethodLayout,
  PaymentMethodType,
} from './types';

const VALID_PAYMENT_METHODS = new Set<PaymentMethodType>([
  'card',
  'apple_pay',
  'google_pay',
]);

export function resolvePaymentMethodLayout(
  value: PaymentMethodLayout | string | undefined,
): PaymentMethodLayout {
  if (value === 'verticalSelector') return 'verticalSelector';
  if (value === 'horizontalSelector') return 'horizontalSelector';
  return 'stacked';
}

export function resolveCardFieldLayout(
  value: CardFieldLayout | string | undefined,
): CardFieldLayout {
  return value === 'split' ? 'split' : 'compact';
}

/**
 * Sanitize merchant paymentMethodOrder. Invalid / duplicate entries are dropped.
 */
export function resolvePaymentMethodOrder(
  value: unknown,
): PaymentMethodType[] | undefined {
  if (!Array.isArray(value) || value.length === 0) return undefined;

  const seen = new Set<PaymentMethodType>();
  const ordered: PaymentMethodType[] = [];

  for (const item of value) {
    if (typeof item !== 'string') continue;
    const method = item.trim() as PaymentMethodType;
    if (!VALID_PAYMENT_METHODS.has(method) || seen.has(method)) continue;
    seen.add(method);
    ordered.push(method);
  }

  return ordered.length > 0 ? ordered : undefined;
}

/**
 * Sanitize the per-session paymentMethods enable/disable overrides. Only
 * known method keys with a boolean `enabled` are kept; everything else
 * (invalid keys, non-boolean `enabled`, malformed entries) is dropped.
 */
export function resolvePaymentMethods(
  value: PaymentElementOptions['paymentMethods'],
): PaymentElementOptions['paymentMethods'] | undefined {
  if (!value || typeof value !== 'object') return undefined;

  const resolved: Partial<Record<PaymentMethodType, { enabled: boolean }>> =
    {};
  for (const method of VALID_PAYMENT_METHODS) {
    const entry = (value as Record<string, unknown>)[method];
    if (
      entry &&
      typeof entry === 'object' &&
      typeof (entry as { enabled?: unknown }).enabled === 'boolean'
    ) {
      resolved[method] = { enabled: (entry as { enabled: boolean }).enabled };
    }
  }

  return Object.keys(resolved).length > 0 ? resolved : undefined;
}

export function resolvePaymentElementOptions(input: {
  options?: PaymentElementOptions;
  buttonText?: string;
  placeholders?: PaymentElementOptions['placeholders'];
  showDefaultSuccessUI?: boolean;
}): PaymentElementOptions {
  const nested = input.options ?? {};
  const cardFormPlaceholders =
    nested.cardForm?.placeholders ??
    nested.placeholders ??
    input.placeholders;
  const paymentMethodOrder = resolvePaymentMethodOrder(
    nested.paymentMethodOrder,
  );
  const paymentMethods = resolvePaymentMethods(nested.paymentMethods);

  const wallets = resolveWalletsOptions(nested.wallets);

  return {
    paymentMethodLayout: resolvePaymentMethodLayout(
      nested.paymentMethodLayout ?? 'stacked',
    ),
    cardFieldLayout: resolveCardFieldLayout(
      nested.cardFieldLayout ?? 'compact',
    ),
    ...(paymentMethodOrder ? { paymentMethodOrder } : {}),
    ...(paymentMethods ? { paymentMethods } : {}),
    ...(nested.buttonText ?? input.buttonText
      ? { buttonText: nested.buttonText ?? input.buttonText }
      : {}),
    ...(wallets ? { wallets } : {}),
    ...(cardFormPlaceholders
      ? {
          cardForm: { placeholders: cardFormPlaceholders },
          placeholders: cardFormPlaceholders,
        }
      : {}),
    ...(nested.showDefaultSuccessUI ?? input.showDefaultSuccessUI !== undefined
      ? {
          showDefaultSuccessUI:
            nested.showDefaultSuccessUI ?? input.showDefaultSuccessUI,
        }
      : {}),
    ...(nested.fillParent !== undefined
      ? { fillParent: nested.fillParent === true }
      : {}),
  };
}

function resolveWalletsOptions(
  value: PaymentElementOptions['wallets'] | undefined,
): PaymentElementOptions['wallets'] | undefined {
  if (!value || typeof value !== 'object') return undefined;

  const buttonTextSrc =
    value.buttonText && typeof value.buttonText === 'object'
      ? value.buttonText
      : undefined;
  const applePay =
    typeof buttonTextSrc?.applePay === 'string' && buttonTextSrc.applePay.trim()
      ? buttonTextSrc.applePay.trim()
      : undefined;
  const googlePay =
    typeof buttonTextSrc?.googlePay === 'string' &&
    buttonTextSrc.googlePay.trim()
      ? buttonTextSrc.googlePay.trim()
      : undefined;
  const buttonTheme =
    value.buttonTheme === 'black' ||
    value.buttonTheme === 'white' ||
    value.buttonTheme === 'auto'
      ? value.buttonTheme
      : undefined;

  const buttonText =
    applePay || googlePay
      ? {
          ...(applePay ? { applePay } : {}),
          ...(googlePay ? { googlePay } : {}),
        }
      : undefined;

  if (!buttonText && !buttonTheme) {
    return undefined;
  }

  return {
    ...(buttonText ? { buttonText } : {}),
    ...(buttonTheme ? { buttonTheme } : {}),
  };
}
